import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Upload, 
  Music, 
  Mic, 
  Download, 
  Loader2,
  CheckCircle,
  AlertCircle,
  X,
  AudioWaveform,
  ArrowLeft,
  Trash2,
  Clock,
  FileAudio
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import SEOHead from '../components/SEOHead';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

interface SeparationResult {
  vocals_download_url: string;
  instrumentals_download_url: string;
  vocals_file_size: number;
  instrumentals_file_size: number;
  method: string;
  quality: string;
  separation_id?: string;
  thumbnail_url?: string;
}

interface SavedVocalSeparation {
  separation_id: string;
  original_filename: string;
  vocals_download_url: string;
  instrumentals_download_url: string;
  vocals_file_size: number;
  instrumentals_file_size: number;
  method: string;
  quality: string;
  thumbnail_url?: string;
  created_at: string;
}

interface VocalSeparationListResponse {
  separations: SavedVocalSeparation[];
  total_count: number;
}

const AudioSeparation: React.FC = () => {
  const { currentUser } = useAuth();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [separationResult, setSeparationResult] = useState<SeparationResult | null>(null);
  const [isDownloading, setIsDownloading] = useState<{vocals: boolean, instrumentals: boolean}>({
    vocals: false,
    instrumentals: false
  });
  const [progress, setProgress] = useState(0);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [speedMode, setSpeedMode] = useState<'fast' | 'balanced' | 'quality'>('balanced');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // Saved separations state
  const [savedSeparations, setSavedSeparations] = useState<SavedVocalSeparation[]>([]);
  const [isLoadingSeparations, setIsLoadingSeparations] = useState(false);
  const [showSavedSeparations, setShowSavedSeparations] = useState(false); // Will be set to true if user has history
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type (accept both audio and video files)
      if (!file.type.startsWith('audio/') && !file.type.startsWith('video/')) {
        toast.error('Please select an audio or video file');
        return;
      }
      
      // Validate file size (50MB limit for audio processing)
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        toast.error('File size must be less than 50MB');
        return;
      }
      
      setSelectedFile(file);
      setSeparationResult(null);
      setProgress(0);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && (file.type.startsWith('audio/') || file.type.startsWith('video/'))) {
      setSelectedFile(file);
      setSeparationResult(null);
      setProgress(0);
    } else {
      toast.error('Please drop an audio or video file');
    }
  };

  const handleSeparation = async () => {
    if (!selectedFile) {
      toast.error('Please select an audio or video file first');
      return;
    }

    if (!currentUser) {
      toast.error('Please sign in to use AI vocal separation');
      return;
    }

    setIsProcessing(true);
    setProgress(0);

    // Generate a unique processing ID
    const newProcessingId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    setProcessingId(newProcessingId);

    // Create abort controller for cancellation
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    let websocket: WebSocket | null = null;
    let progressInterval: NodeJS.Timeout | null = null;

    try {
      // Set up WebSocket for real-time progress
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${window.location.host}/api/v1/tools/vocal-separation/progress/${newProcessingId}`;
      
      try {
        websocket = new WebSocket(wsUrl);
        
        websocket.onopen = () => {
          // console.log('WebSocket connection established for vocal separation progress');
        };
        
        websocket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'progress' && data.progress) {
              const { percent, message: _message } = data.progress;
              setProgress(percent || 0);
              // console.log(`WebSocket Progress: ${percent}% - ${message}`);
            }
          } catch (e) {
            console.error('Error parsing WebSocket message:', e);
          }
        };

        websocket.onclose = (_event) => {
          // console.log('WebSocket connection closed:', event.code, event.reason);
        };

        websocket.onerror = (error) => {
          console.warn('WebSocket error, falling back to polling:', error);
          websocket = null;
        };
        
        // Wait a moment for WebSocket to establish connection
        await new Promise(resolve => setTimeout(resolve, 100));
        
      } catch (e) {
        console.warn('WebSocket connection failed, using polling fallback:', e);
        websocket = null;
      }

      // Always start HTTP polling as backup (WebSocket might fail silently)
      progressInterval = setInterval(async () => {
        try {
          const token = await currentUser.getIdToken();
          const progressResponse = await fetch(`/api/v1/tools/vocal-separation/progress/${newProcessingId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (progressResponse.ok) {
            const progressData = await progressResponse.json();
            if (progressData.progress) {
              setProgress(progressData.progress.percent || 0);
              // console.log(`Polling Progress: ${progressData.progress.percent}% - ${progressData.progress.message}`);
            }
          }
        } catch (e) {
          console.error('Progress polling error:', e);
        }
      }, 2000); // Poll every 2 seconds

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('processing_id', newProcessingId);
      formData.append('speed_mode', speedMode);

      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/tools/vocal-separation', {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: abortController.signal
      });

      // Clean up progress tracking
      if (websocket) {
        websocket.close();
        websocket = null;
      }
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Vocal separation failed');
      }

      const result: SeparationResult = await response.json();
      setSeparationResult(result);
      setProgress(100);
      
      // If separation was saved to Firebase, refresh the saved separations list
      if (result.separation_id && showSavedSeparations) {
        loadSavedSeparations();
      }
      
      toast.success('Vocal separation completed! Download your files below.');

    } catch (error) {
      // Clean up on error
      if (websocket) {
        websocket.close();
      }
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      
      if (error instanceof Error && error.name === 'AbortError') {
        // Request was cancelled, don't show error
        return;
      }
      
      console.error('Separation error:', error);
      toast.error(error instanceof Error ? error.message : 'Vocal separation failed');
    } finally {
      setIsProcessing(false);
      setProcessingId(null);
      abortControllerRef.current = null;
    }
  };

  const clearFile = async () => {
    // If processing is active, cancel it first
    if (isProcessing) {
      await cancelProcessing();
    }
    
    setSelectedFile(null);
    setSeparationResult(null);
    setProgress(0);
    setProcessingId(null);
    setIsDownloading({ vocals: false, instrumentals: false });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const cancelProcessing = async () => {
    if (!processingId || !abortControllerRef.current) return;
    
    try {
      // Abort the fetch request
      abortControllerRef.current.abort();
      
      // Notify backend to cancel processing
      const token = await currentUser?.getIdToken();
      if (token) {
        fetch(`/api/v1/tools/vocal-separation/cancel/${processingId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }).catch(() => {}); // Ignore errors, it's best effort
      }
      
      toast.info('Processing cancelled');
    } catch (error) {
      console.error('Cancel error:', error);
    } finally {
      setIsProcessing(false);
      setProgress(0);
      setProcessingId(null);
      abortControllerRef.current = null;
    }
  };

  const downloadFile = async (url: string, filename: string, type: 'vocals' | 'instrumentals') => {
    setIsDownloading(prev => ({ ...prev, [type]: true }));
    
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} download started!`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error(`Failed to download ${type}`);
    } finally {
      setIsDownloading(prev => ({ ...prev, [type]: false }));
    }
  };

  // Load saved separations
  const loadSavedSeparations = useCallback(async () => {
    if (!currentUser) return;
    
    setIsLoadingSeparations(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/tools/vocal-separations', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data: VocalSeparationListResponse = await response.json();
        setSavedSeparations(data.separations);
      } else {
        console.error('Failed to load saved separations');
      }
    } catch (error) {
      console.error('Error loading saved separations:', error);
    } finally {
      setIsLoadingSeparations(false);
    }
  }, [currentUser]);

  // Delete a saved separation
  const deleteSeparation = async (separationId: string) => {
    if (!currentUser) return;
    
    setDeletingIds(prev => {
      const newSet = new Set(prev);
      newSet.add(separationId);
      return newSet;
    });
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/v1/tools/vocal-separations/${separationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setSavedSeparations(prev => prev.filter(sep => sep.separation_id !== separationId));
        toast.success('Vocal separation deleted successfully');
      } else {
        toast.error('Failed to delete vocal separation');
      }
    } catch (error) {
      console.error('Error deleting separation:', error);
      toast.error('Failed to delete vocal separation');
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(separationId);
        return newSet;
      });
    }
  };

  // Load saved separations when component mounts and user is available
  useEffect(() => {
    if (currentUser) {
      loadSavedSeparations();
    }
  }, [currentUser, loadSavedSeparations]);

  // Update showSavedSeparations based on whether user has history
  useEffect(() => {
    if (savedSeparations.length > 0) {
      setShowSavedSeparations(true);
    } else {
      setShowSavedSeparations(false);
    }
  }, [savedSeparations.length]);

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown date';
    }
  };

  return (
    <>
      <SEOHead 
        title="AI Vocal Separation - ClipThis"
        description="Separate vocals from instrumentals using advanced AI. Extract clean vocals and backing tracks from any audio or video file."
        keywords="vocal separation, AI audio processing, karaoke, instrumentals, vocals, audio extraction"
      />
      <BreadcrumbSchema items={[
        { name: 'AI Vocal Separation', path: '/audio' }
      ]} />
      
      <div className="min-h-screen relative overflow-x-hidden" style={{ minHeight: 'calc(100vh - 64px)' }}>
        {/* Animated background */}
        <div className="absolute inset-0 dot-pattern-animated opacity-30 pointer-events-none" />
        
        <div className="relative z-10 px-6 sm:px-8 pt-4 animate-fade-up">
          {/* Back Button */}
          <Link 
            to={currentUser ? "/dashboard" : "/"}
            className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors mb-8"
          >
            <ArrowLeft size={20} />
            <span>Back to {currentUser ? "Dashboard" : "Home"}</span>
          </Link>
        
        {/* Hero Section */}
        <div className="py-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center">
                  <Music className="w-8 h-8 text-white" />
                </div>
              </div>
              <h1 className="text-5xl font-display font-bold tracking-tight mb-4">
                AI Vocal Separation
              </h1>
              <p className="text-xl text-primary-600 max-w-3xl mx-auto">
                Separate vocals from instrumentals using advanced AI technology. Extract clean vocals and backing tracks from any audio or video file.
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto py-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-primary-100 shadow-sm">
            <div className="p-8">
              <div className="max-w-4xl mx-auto space-y-8">
                
                {/* Sign-in required banner */}
                {!currentUser && (
                  <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl p-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Music className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-red-800 font-semibold mb-1">Sign In Required</h3>
                        <p className="text-red-700 text-sm">
                          AI vocal separation is available for signed-in users only. This feature uses advanced AI models that require authentication.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* File Upload Section */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Audio or Video File</h3>
                  
                  {!selectedFile ? (
                    <div
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors"
                    >
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-lg font-medium text-gray-900 mb-2">
                        Drop your audio/video file here or click to browse
                      </p>
                  <p className="text-sm text-gray-600">
                    Supports MP3, WAV, MP4, MOV, and other audio/video formats up to 50MB and 8 minutes duration
                  </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="audio/*,video/*"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-gray-200 rounded-lg">
                            {selectedFile.type.startsWith('audio/') ? (
                              <Music className="w-6 h-6 text-gray-700" />
                            ) : (
                              <AudioWaveform className="w-6 h-6 text-gray-700" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{selectedFile.name}</p>
                            <p className="text-sm text-gray-600">
                              {(selectedFile.size / (1024 * 1024)).toFixed(1)} MB • {selectedFile.type.startsWith('audio/') ? 'Audio' : 'Video'} File
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={clearFile}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Download History Section */}
                {currentUser && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <FileAudio className="w-5 h-5 mr-2 text-red-600" />
                        Your Download History ({savedSeparations.length})
                      </h3>
                      {savedSeparations.length > 0 && (
                        <button
                          onClick={() => {
                            setShowSavedSeparations(!showSavedSeparations);
                            if (!showSavedSeparations) {
                              loadSavedSeparations();
                            }
                          }}
                          className="text-red-600 hover:text-red-700 font-medium text-sm transition-colors"
                        >
                          {showSavedSeparations ? 'Hide History' : 'Show History'}
                        </button>
                      )}
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      {isLoadingSeparations ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-red-600" />
                          <span className="ml-2 text-gray-600">Loading saved separations...</span>
                        </div>
                      ) : savedSeparations.length === 0 ? (
                        <div className="text-center py-8">
                          <FileAudio className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-gray-600">No download history yet</p>
                          <p className="text-gray-500 text-sm">Your completed vocal separations will appear here for easy re-download</p>
                        </div>
                      ) : showSavedSeparations ? (
                        <div className="grid gap-4">
                          {savedSeparations.map((separation) => (
                            <div key={separation.separation_id} className="bg-white rounded-lg border border-gray-200 p-4">
                              <div className="flex items-start space-x-4">
                                {/* Thumbnail */}
                                <div className="flex-shrink-0">
                                  {separation.thumbnail_url ? (
                                    <img
                                      src={separation.thumbnail_url}
                                      alt="Waveform"
                                      className="w-20 h-12 object-cover rounded border border-gray-200"
                                    />
                                  ) : (
                                    <div className="w-20 h-12 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                                      <AudioWaveform className="w-6 h-6 text-gray-400" />
                                    </div>
                                  )}
                                </div>

                                {/* Details */}
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-gray-900 truncate">
                                    {separation.original_filename}
                                  </h4>
                                  <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                                    <span className="flex items-center">
                                      <Clock className="w-4 h-4 mr-1" />
                                      {formatDate(separation.created_at)}
                                    </span>
                                    <span>{separation.method}</span>
                                    <span>{separation.quality} quality</span>
                                  </div>
                                  <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                                    <span>Vocals: {(separation.vocals_file_size / (1024 * 1024)).toFixed(1)} MB</span>
                                    <span>Instrumentals: {(separation.instrumentals_file_size / (1024 * 1024)).toFixed(1)} MB</span>
                                  </div>
                                </div>

                                  {/* Actions */}
                                  <div className="flex flex-col space-y-2">
                                    <button
                                      onClick={() => downloadFile(
                                        separation.vocals_download_url,
                                        `vocals_${separation.original_filename.replace(/\.[^/.]+$/, '')}.wav`,
                                        'vocals'
                                      )}
                                      className="flex items-center px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm"
                                      title="Download Vocals"
                                    >
                                      <Mic className="w-4 h-4 mr-2" />
                                      Vocals
                                    </button>
                                    <button
                                      onClick={() => downloadFile(
                                        separation.instrumentals_download_url,
                                        `instrumentals_${separation.original_filename.replace(/\.[^/.]+$/, '')}.wav`,
                                        'instrumentals'
                                      )}
                                      className="flex items-center px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm"
                                      title="Download Instrumentals"
                                    >
                                      <Music className="w-4 h-4 mr-2" />
                                      Instrumentals
                                    </button>
                                    <button
                                      onClick={() => deleteSeparation(separation.separation_id)}
                                      disabled={deletingIds.has(separation.separation_id)}
                                      className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 text-sm"
                                      title="Delete Separation"
                                    >
                                      {deletingIds.has(separation.separation_id) ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                      ) : (
                                        <Trash2 className="w-4 h-4 mr-2" />
                                      )}
                                      Delete
                                    </button>
                                  </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-gray-600">Click "Show History" to view your {savedSeparations.length} saved separation{savedSeparations.length !== 1 ? 's' : ''}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Speed Mode Selection */}
                {selectedFile && currentUser && !separationResult && !isProcessing && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Processing Speed</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <button
                        onClick={() => setSpeedMode('fast')}
                        className={`p-4 rounded-lg border-2 transition-colors ${
                          speedMode === 'fast' 
                            ? 'border-red-500 bg-red-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-center">
                          <div className="w-8 h-8 mx-auto mb-2 text-red-500">⚡</div>
                          <h4 className="font-medium text-gray-900">Fast</h4>
                          <p className="text-sm text-gray-600">~30-90 seconds</p>
                          <p className="text-xs text-gray-500 mt-1">Good quality</p>
                        </div>
                      </button>
                      
                      <button
                        onClick={() => setSpeedMode('balanced')}
                        className={`p-4 rounded-lg border-2 transition-colors ${
                          speedMode === 'balanced' 
                            ? 'border-red-500 bg-red-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-center">
                          <div className="w-8 h-8 mx-auto mb-2 text-black">⚖️</div>
                          <h4 className="font-medium text-gray-900">Balanced</h4>
                          <p className="text-sm text-gray-600">~2-4 minutes</p>
                          <p className="text-xs text-gray-500 mt-1">Great quality</p>
                        </div>
                      </button>
                      
                      <button
                        onClick={() => setSpeedMode('quality')}
                        className={`p-4 rounded-lg border-2 transition-colors ${
                          speedMode === 'quality' 
                            ? 'border-red-500 bg-red-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-center">
                          <div className="w-8 h-8 mx-auto mb-2 text-primary-600">💎</div>
                          <h4 className="font-medium text-gray-900">Quality</h4>
                          <p className="text-sm text-gray-600">~4-8 minutes</p>
                          <p className="text-xs text-gray-500 mt-1">Best quality</p>
                        </div>
                      </button>
                    </div>
                  </div>
                )}

                {/* Separation Action */}
                {selectedFile && currentUser && (
                  <div>
                    {!separationResult && !isProcessing && (
                      <button
                        onClick={handleSeparation}
                        disabled={!selectedFile || !currentUser}
                        className={`w-full py-3 px-6 rounded-lg font-medium transition-colors flex items-center justify-center ${
                          selectedFile && currentUser
                            ? 'bg-red-600 text-white hover:bg-red-700' 
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        <Music className="w-5 h-5 mr-2" />
                        {!currentUser 
                          ? 'Sign in to separate vocals' 
                          : !selectedFile
                            ? 'Upload a file to continue'
                            : `Separate Vocals & Instrumentals (${speedMode.charAt(0).toUpperCase() + speedMode.slice(1)} Mode)`
                        }
                      </button>
                    )}

                    {isProcessing && (
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-4">
                          <Loader2 className="w-8 h-8 text-red-600 animate-spin" />
                        </div>
                        <p className="text-lg font-medium text-gray-900 mb-2">Processing Audio...</p>
                        <p className="text-sm text-gray-600 mb-4">
                          Using AI to separate vocals from instrumentals
                        </p>
                        <div className="bg-gray-200 rounded-lg h-2 mb-4">
                          <div
                            className={`bg-red-600 h-2 transition-all duration-300 ${progress >= 100 ? 'rounded-lg' : 'rounded-l-lg'}`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500">
                          {speedMode === 'fast' && 'Processing time: ~30-90 seconds regardless of length'}
                          {speedMode === 'balanced' && 'Processing time: ~2-4 minutes (optimized AI)'}
                          {speedMode === 'quality' && 'Processing time: ~4-8 minutes (highest quality)'}
                        </p>
                      </div>
                    )}

                    {separationResult && (
                      <div className="text-center space-y-6">
                        <div className="flex items-center justify-center mb-4">
                          <CheckCircle className="w-8 h-8 text-red-600" />
                        </div>
                        <p className="text-lg font-medium text-gray-900">Separation Complete!</p>
                        <p className="text-sm text-gray-600 mb-4">
                          Your audio has been separated into vocals and instrumentals using {separationResult.method}.
                        </p>
                        
                        <div className="grid md:grid-cols-2 gap-4">
                          {/* Vocals Download */}
                          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className="flex items-center justify-center mb-3">
                              <div className="p-3 bg-gray-200 rounded-full">
                                <Mic className="w-6 h-6 text-gray-700" />
                              </div>
                            </div>
                            <h4 className="font-medium text-gray-900 mb-1">Vocals Only</h4>
                            <p className="text-sm text-gray-600 mb-3">
                              {(separationResult.vocals_file_size / (1024 * 1024)).toFixed(1)} MB
                            </p>
                            <button
                              onClick={() => downloadFile(
                                separationResult.vocals_download_url,
                                `vocals_${selectedFile?.name?.replace(/\.[^/.]+$/, '')}.wav`,
                                'vocals'
                              )}
                              disabled={isDownloading.vocals}
                              className="w-full py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center"
                            >
                              {isDownloading.vocals ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Downloading...
                                </>
                              ) : (
                                <>
                                  <Download className="w-4 h-4 mr-2" />
                                  Download Vocals
                                </>
                              )}
                            </button>
                          </div>

                          {/* Instrumentals Download */}
                          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className="flex items-center justify-center mb-3">
                              <div className="p-3 bg-gray-200 rounded-full">
                                <Music className="w-6 h-6 text-gray-700" />
                              </div>
                            </div>
                            <h4 className="font-medium text-gray-900 mb-1">Instrumentals Only</h4>
                            <p className="text-sm text-gray-600 mb-3">
                              {(separationResult.instrumentals_file_size / (1024 * 1024)).toFixed(1)} MB
                            </p>
                            <button
                              onClick={() => downloadFile(
                                separationResult.instrumentals_download_url,
                                `instrumentals_${selectedFile?.name?.replace(/\.[^/.]+$/, '')}.wav`,
                                'instrumentals'
                              )}
                              disabled={isDownloading.instrumentals}
                              className="w-full py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center"
                            >
                              {isDownloading.instrumentals ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Downloading...
                                </>
                              ) : (
                                <>
                                  <Download className="w-4 h-4 mr-2" />
                                  Download Instrumentals
                                </>
                              )}
                            </button>
                          </div>
                        </div>

                        <button
                          onClick={clearFile}
                          className="block w-full text-gray-600 hover:text-gray-800 text-sm py-2"
                        >
                          Separate another file
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Tips Section */}
                <div className="border-t border-gray-200 pt-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2 text-gray-600" />
                    How It Works & Tips
                  </h3>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <strong>State-of-the-art AI: </strong> Uses Demucs for professional studio-quality separation
                    </p>
                    <p className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <strong>File storage: </strong> Max 50MB and 8 minutes duration. Results saved to Firebase Storage with waveform thumbnails
                    </p>
                    <p className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <strong>Best results: </strong> Works excellently with studio recordings where vocals are centered
                    </p>
                    <p className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <strong>Perfect for: </strong> Karaoke tracks, remixes, vocal sampling, and music production
                    </p>
                    <p className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <strong>Saved separations: </strong> Access your previous separations with thumbnails. Download or delete them anytime
                    </p>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default AudioSeparation;